import data from '../../shared/service-catalog.json' with { type: 'json' };
import type { Language } from '../i18n';

export type CatalogItem = { id: string; group: string; setup: number | null; monthly: number | null; requires: string[]; replaces?: string; review?: boolean; labels: string[]; details: string[] };
export const catalog: CatalogItem[] = data.items;
export const catalogVersion = data.version;
export const projectAreaIds = ['website', 'app', 'automation', 'ai', 'flowmate'] as const;
const localeIndex: Record<Language, number> = { pl: 0, en: 1, uk: 2, ru: 3 };
export function itemLabel(item: CatalogItem, language: Language) { return item.labels[localeIndex[language]]; }
export function itemDetail(item: CatalogItem, language: Language) { return item.details[localeIndex[language]]; }
export function advisorAddonPrice(id: string) {
  const item = catalog.find(item => item.id === id);
  if (!item || item.id === 'flowmate' || item.group === 'delivery' || ['site-one', 'site-company', 'site-shop'].includes(id) || !item.setup) return null;
  const setup = Math.round(item.setup * .8);
  return { original: item.setup, setup, saving: item.setup - setup };
}

export function normalizeModules(ids: readonly string[]): string[] {
  const chosen = new Set(ids.filter(id => catalog.some(item => item.id === id)));
  for (const item of catalog) if (chosen.has(item.id)) for (const dependency of item.requires) chosen.add(dependency);
  for (const item of catalog) if (chosen.has(item.id) && item.replaces) chosen.delete(item.replaces);
  return catalog.filter(item => chosen.has(item.id)).map(item => item.id);
}

export function toggleModule(ids: readonly string[], id: string): string[] {
  const item = catalog.find(value => value.id === id);
  if (!item) return normalizeModules(ids);
  const chosen = new Set(normalizeModules(ids));
  if (chosen.has(id)) {
    chosen.delete(id);
    for (const entry of catalog) if (entry.requires.includes(id)) chosen.delete(entry.id);
  } else {
    chosen.add(id);
    if (id === 'website-form') chosen.delete('photo-form');
  }
  return normalizeModules([...chosen]);
}

export function estimateProject(areas: readonly string[], modules: readonly string[]) {
  const items = normalizeModules(modules).map(id => catalog.find(item => item.id === id)!);
  const customProject = areas.some(id => id !== 'flowmate' && projectAreaIds.includes(id as typeof projectAreaIds[number]));
  return {
    items,
    setup: items.reduce((sum, item) => sum + (item.setup ?? 0), 0),
    monthly: items.reduce((sum, item) => sum + (item.monthly ?? 0), 0),
    setupUnknown: customProject || items.some(item => item.setup === null) || (!items.some(item => item.id === 'flowmate') && items.length > 0),
    monthlyUnknown: customProject || items.some(item => item.monthly === null) || (!items.some(item => item.id === 'flowmate') && items.length > 0),
    requiresReview: items.some(item => item.review),
  };
}

export function formatProjectMoney(amount: number, language: Language) {
  return new Intl.NumberFormat(language, { style: 'currency', currency: 'PLN', maximumFractionDigits: 0 }).format(amount);
}

// Display-only reference, not a live exchange rate or a separate USD price list.
export const usdReferencePln = 4;
export const usdEstimateNote = 'USD estimates use a fixed reference of 1 USD = 4 PLN, not a live exchange rate. Quotes and billing remain in PLN.';
export function projectUsdAmount(amount: number) {
  return Math.round(amount / usdReferencePln * 100) / 100;
}
export function formatProjectUsd(amount: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', currencyDisplay: 'code' }).format(projectUsdAmount(amount));
}
